import { EUserAccessPart, IUserInfoDto } from "@kuika/kuika-cl-model";
import { EWorkspaceRole, IAssignWorkspaceRoleToUserRequest } from "@kuika/kuika-cl-model/src/dtos/user-info.dto";
import { IWorkspaceRoleDto } from "@kuika/kuika-cl-model/src/dtos/workspace-role.dto";
import _ from "lodash";
import * as React from "react";
import { UserService } from "../../../services/user-service";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import KuikaAvatar, { getIdentitySeed } from "../../ui-kit/kuika-avatar/kuika-avatar";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaRoleBadge from "../../ui-kit/kuika-role-badge/kuika-role-badge";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { EMemberInfoType } from "./member-info-modal";
import { formatMemberSince, IUserInfoWithCreatedAt } from "./profile-member-since";

export interface IState {
  selectedRoles: any[];
  isDropdownVisible: boolean;
  workspaceRoles: IWorkspaceRoleDto[];
}

export interface IProps {
  userInfo: IUserInfoDto;
  onMenuClick?: () => void;
  type: EMemberInfoType;
  handleGetUserById?: () => void;
  updateUserList?: () => void;
  /**
   * `data-testid` for the address. Passed by the caller that knows whose profile this is — the My
   * Profile modal — because the same view also shows other members in the member-info modal.
   */
  emailTestId?: string;
}

class ProfileUserInfo extends React.PureComponent<IProps & IWithContextProp, IState> {
  constructor(props: IProps & IWithContextProp) {
    super(props);
    this.state = {
      selectedRoles: [],
      isDropdownVisible: false,
      workspaceRoles: []
    };
  }

  /**
   * Only the Change-role dropdown uses this list, and that dropdown is drawn under exactly
   * three conditions. On My profile the viewer is always the subject, so `isCurrentUser` is
   * true and the dropdown can never open — the request was paid for on every open of that
   * dialog and thrown away.
   */
  canChangeWorkspaceRole = (): boolean => {
    const isCurrentUser: boolean = this.props.userInfo?.id === this.props.context.userInfo?.id;
    return (
      KMainFunctions.isDomainAuthorizationEnabled() &&
      KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.AddEditRole) &&
      !isCurrentUser
    );
  };

  async componentDidMount() {
    const canChangeRole: boolean = this.canChangeWorkspaceRole();
    if (!canChangeRole) {
      return;
    }
    try {
      const res: any = await UserService.GetWorkspaceRoles();
      if (res && res.status === 200) {
        this.setState({
          workspaceRoles: res.data?.filter(
            (role: IWorkspaceRoleDto) =>
              role.enumId !== EWorkspaceRole.ComponentDesigner &&
              role.enumId !== EWorkspaceRole.ComponentDeveloper &&
              role.enumId !== EWorkspaceRole.DesignOps
          )
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  componentDidUpdate(prevProps: IProps): void {
    if (prevProps.userInfo !== this.props.userInfo) {
      const selectedRoles = this.props.userInfo.workspaceRoles?.map((role) => {
        return {
          id: role.enumId,
          name: role.name
        };
      });
      this.setState({ selectedRoles });
    }
  }

  handleAssignWorkspaceRoleToUser = async () => {
    const isRolesEqual = _.isEqual(
      this.state.selectedRoles.map((role) => role.id),
      this.props.userInfo.workspaceRoles.map((role) => role.enumId)
    );
    if (isRolesEqual) {
      this.setState({ isDropdownVisible: false });
      return;
    }
    if (this.state.selectedRoles.length === 0) {
      KMainFunctions.displayWarningNotification("Please select at least one role");
      return;
    }
    this.setState({ isDropdownVisible: false });
    try {
      const res = await UserService.AssignWorkspaceRoleToUser({
        userId: this.props.userInfo.id,
        workSpaceRoleEnumIds: this.state.selectedRoles.map((role) => role.id)
      } as IAssignWorkspaceRoleToUserRequest);
      if (res) {
        KMainFunctions.displaySuccessNotification("Role(s) assigned successfully");
        this.props.handleGetUserById && this.props.handleGetUserById();
        this.props.updateUserList && this.props.updateUserList();
      }
    } catch (e) {
      KMainFunctions.displayErrorNotification(e);
    }
  };

  /**
   * A multi-select: every row toggles and the menu stays open, because the choice is not
   * committed until it closes (`handleAssignWorkspaceRoleToUser`). `keepOpenOnSelect` is what
   * the primitive gives for exactly that.
   */
  getChangeWorkspaceRoleOptions = (): KuikaDropdownMenuItemType[] =>
    (this.state.workspaceRoles ?? [])
      .filter((role: IWorkspaceRoleDto): boolean => role.enumId !== EWorkspaceRole.WorkspaceOwner)
      .map((role: IWorkspaceRoleDto): KuikaDropdownMenuItemType => {
        const isSelected: boolean = Boolean(this.state.selectedRoles.find((x) => x.id === role.enumId));

        return {
          key: role.id,
          label: role.name,
          // Several roles can be held at once, so the primitive's single `selected` key does
          // not describe this menu — but the mark is the theme-aware one, and the row states
          // its own state through a class rather than an inline literal background.
          icon: <KuikaSvg name="check" width={14} height={14} color={isSelected ? "var(--blue-5)" : "transparent"} />,
          dataTestId: `workspaceRoleOption-${role.enumId}`,
          keepOpenOnSelect: true,
          onClick: (): void =>
            this.setState({
              selectedRoles: isSelected
                ? this.state.selectedRoles.filter((x) => x.id !== role.enumId)
                : [...this.state.selectedRoles, { id: role.enumId, name: role.name }]
            })
        };
      });

  // Same value the Team table shows as "Registration Date". Fixed to en-US so
  // the month name does not shift with the developer's or server's locale.
  render() {
    const isDomainAuthorizationEnabled = KMainFunctions.isDomainAuthorizationEnabled();
    const canChangeRole: boolean = this.canChangeWorkspaceRole();

    const userFullName = `${this.props.userInfo?.firstName ?? ""} ${this.props.userInfo?.lastName ?? ""}`;
    const photoUrl: string = this.props.context.profilePhoto || this.props.userInfo.profilePhotoUrl;
    const memberSince: string = formatMemberSince((this.props.userInfo as IUserInfoWithCreatedAt)?.createdAt);
    const hasActions: boolean = Boolean(this.props.onMenuClick);
    return (
      <div className="kuika_profile__user-infobox">
        <KuikaAvatar
          size={64}
          src={photoUrl}
          // The id first: a colour tied to the display name changed when the person did.
          seed={getIdentitySeed(this.props.userInfo)}
        >
          {KMainFunctions.getUserLettersToShow(this.props.userInfo)}
        </KuikaAvatar>
        <div className="user-infobox__details">
          <div className="name-info">
            <span className="name-info__full-name" title={userFullName}>
              {userFullName}
            </span>
            <span
              className="name-info__email"
              title={this.props.userInfo?.username}
              data-testid={this.props.emailTestId}
            >
              {this.props.userInfo?.username}
            </span>
            {memberSince && <span className="name-info__since">Member since {memberSince}</span>}
          </div>
          {isDomainAuthorizationEnabled && (
            <div className="role-info">
              <div className="role-info__head">
                <span className="role-info__title">Workspace Role(s)</span>
                {canChangeRole && (
                  <KuikaDropdownMenu
                    options={this.getChangeWorkspaceRoleOptions()}
                    trigger="click"
                    header="Choose roles"
                    overlayClassName="kuika_profile__workspace-role-dropdown"
                    open={this.state.isDropdownVisible}
                    setOpen={(open: boolean): void => {
                      if (!open) {
                        this.handleAssignWorkspaceRoleToUser();
                      } else {
                        this.setState({ isDropdownVisible: open });
                      }
                    }}
                  >
                    <span className="role-info__change">Change</span>
                  </KuikaDropdownMenu>
                )}
              </div>
              <div className="role-info__roles">
                {this.props.userInfo.workspaceRoles?.map((role) => (
                  <KuikaRoleBadge key={role.enumId} role={role.name} />
                ))}
              </div>
            </div>
          )}
        </div>
        {/* The three-dot menu held exactly one item — Update Profile — and
            Change Password was reachable only from inside the edit form. Both
            are first-level actions, so they are shown, not hidden. */}
        {hasActions && (
          <div className="user-infobox__actions">
            {this.props.onMenuClick && (
              <button type="button" data-testid="myProfileUpdateProfileButton" onClick={this.props.onMenuClick}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M4 20h4l10-10-4-4L4 16v4z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
                Update profile
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
}

const profileUserInfo = withContext<IProps>(ProfileUserInfo);
export { profileUserInfo as ProfileUserInfo };
